import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Kpi } from "@/components/Kpi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Buildings, Package, TruckTrailer, Leaf, Plus, Users, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [overview, setOverview] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", plant_location: "", admin_name: "", admin_email: "", admin_password: "" });

  const load = () => {
    api.get("/tenants").then((r) => setTenants(r.data));
    api.get("/platform/overview").then((r) => setOverview(r.data));
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try { await api.post(`/tenants/${id}/${action}`); toast.success(`Company ${action === "approve" ? "approved" : "rejected"}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const create = async () => {
    try {
      await api.post("/tenants", form);
      toast.success("Tenant onboarded");
      setOpen(false);
      setForm({ name: "", plant_location: "", admin_name: "", admin_email: "", admin_password: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Layout title="Platform Overview" subtitle="Tenant management, usage & sustainability oversight"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-tenant-btn" className="bg-slate-900 hover:bg-slate-800"><Plus size={16} className="mr-1.5" />Onboard Tenant</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Onboard Manufacturer</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              {[
                ["Company name", "name", "text"], ["Plant location", "plant_location", "text"],
                ["Admin name", "admin_name", "text"], ["Admin email", "admin_email", "email"], ["Admin password", "admin_password", "password"],
              ].map(([label, key, type]) => (
                <div key={key}>
                  <Label className="text-xs uppercase font-bold text-slate-500">{label}</Label>
                  <Input data-testid={`t-${key}`} type={type} className="mt-1.5" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                </div>
              ))}
            </div>
            <DialogFooter><Button data-testid="submit-tenant" onClick={create} className="bg-slate-900 hover:bg-slate-800">Create Tenant + Admin</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      }>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi testid="pf-tenants" label="Tenants" value={overview?.total_tenants ?? "—"} icon={Buildings} accent="#0F172A" sub={overview?.pending_tenants ? `${overview.pending_tenants} pending approval` : "All approved"} />
        <Kpi testid="pf-shipments" label="Total Shipments" value={overview?.total_shipments ?? "—"} icon={Package} accent="#2563EB" />
        <Kpi testid="pf-active" label="Active" value={overview?.active_shipments ?? "—"} icon={TruckTrailer} accent="#EAB308" />
        <Kpi testid="pf-co2" label="Platform CO₂" value={overview ? (overview.total_co2_kg / 1000).toFixed(2) : "—"} unit="t" icon={Leaf} accent="#059669" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200"><h3 className="font-heading font-semibold">All Tenants</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-2.5 font-bold">Company</th>
                <th className="px-5 py-2.5 font-bold">Plant</th>
                <th className="px-5 py-2.5 font-bold">Users</th>
                <th className="px-5 py-2.5 font-bold">Shipments</th>
                <th className="px-5 py-2.5 font-bold">Last 30d</th>
                <th className="px-5 py-2.5 font-bold">CO₂</th>
                <th className="px-5 py-2.5 font-bold">Last Active</th>
                <th className="px-5 py-2.5 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} data-testid={`tenant-row-${t.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900 flex items-center gap-2"><div className="h-8 w-8 rounded-md bg-slate-900 text-white flex items-center justify-center"><Buildings size={16} weight="duotone" /></div>{t.name}</td>
                  <td className="px-5 py-3 text-slate-600">{t.plant_location}</td>
                  <td className="px-5 py-3 text-slate-600"><span className="inline-flex items-center gap-1"><Users size={14} className="text-slate-400" />{t.user_count}</span></td>
                  <td className="px-5 py-3 font-mono text-slate-600">{t.shipment_count}</td>
                  <td className="px-5 py-3 font-mono text-slate-600">{t.shipments_this_month ?? 0}</td>
                  <td className="px-5 py-3 font-mono text-slate-600">{(t.total_co2_kg / 1000).toFixed(2)} t</td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{t.last_activity ? new Date(t.last_activity).toLocaleDateString() : "—"}</td>
                  <td className="px-5 py-3">
                    {t.status === "pending" ? (
                      <div className="flex items-center gap-2">
                        <Button size="sm" data-testid={`approve-${t.id}`} onClick={() => act(t.id, "approve")} className="h-7 px-2.5 bg-green-600 hover:bg-green-700"><Check size={14} className="mr-1" />Approve</Button>
                        <Button size="sm" variant="outline" data-testid={`reject-${t.id}`} onClick={() => act(t.id, "reject")} className="h-7 px-2.5 text-red-600 border-red-200 hover:bg-red-50"><X size={14} className="mr-1" />Reject</Button>
                      </div>
                    ) : t.status === "rejected" ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-100 text-red-700 px-2.5 py-0.5 text-xs font-semibold"><span className="h-1.5 w-1.5 rounded-full bg-red-600" />Rejected</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-green-300 bg-green-100 text-green-700 px-2.5 py-0.5 text-xs font-semibold"><span className="h-1.5 w-1.5 rounded-full bg-green-600" />Active</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
